import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next/hooks';
import ReactMapboxGl, { Feature, Layer, Marker, Popup, ZoomControl } from 'react-mapbox-gl';

import { IMAGES } from '../../enum';
import { MAPBOX_LINE_LAYOUT } from '../../enum/Mapbox';
import useDebouncedState from '../../hooks/useDebouncedState';
import useDidUpdateEffect from '../../hooks/useDidUpdateEffect';
import { getMapBoundaries } from '../../utils/map';
import { FinishPopupContent } from './FinishPopup';
import { StartPopupContent } from './StartPopup';

const Map = ReactMapboxGl({
  accessToken: process.env.REACT_APP_MAPBOX_API_TOKEN,
});

const redMarker = <img src={IMAGES.RED_MARK_MAP_ICON} style={{ width: '40px' }} alt="marker" />;

const greenMarker = <img src={IMAGES.GREEN_MARK_MAP_ICON} style={{ width: '40px' }} alt="marker" />;

export const MapboxMap = ({ routes, config, routeVisibility }) => {
  const [t] = useTranslation();
  const [visibleMarkerPopup, setVisibleMarkerPopup] = React.useState();
  const [mapInstance, setMapInstance] = useState();
  const [isMapLoaded, setIsMapLoaded] = useState(false);
  const [debouncedMapBounds, setDebouncedMapBounds] = useDebouncedState([], 500);

  useDidUpdateEffect(() => {
    if (mapInstance) {
      setTimeout(() => {
        mapInstance.resize();
      }, [200]);
    }
  }, [mapInstance]);

  // Determine which routes should be visible
  const visibleRoutes = useMemo(() => {
    if (!routeVisibility) {
      // If no visibility control, show all routes
      return routes;
    }

    // Filter routes based on visibility state
    return routes.filter((route, index) => {
      if (index === 0) return routeVisibility.recorded;
      if (index === 1) return routeVisibility.adjusted;
      return true; // Show any additional routes
    });
  }, [routes, routeVisibility]);

  const polylines = useMemo(
    () =>
      visibleRoutes.map((route, index) => {
        const lastPolyline = route.polyline.map(point => [point.lng, point.lat]);
        const routeColor = route.color || '#6b99f0'; // Default to blue if no color specified

        return (
          <Layer
            key={`route-layer-${index}`}
            id={`route-${index}`}
            source={`route-${index}`}
            type="line"
            layout={MAPBOX_LINE_LAYOUT}
            paint={{
              'line-color': routeColor,
              'line-width': 4,
            }}
          >
            <Feature coordinates={lastPolyline} />
          </Layer>
        );
      }),
    [visibleRoutes],
  );

  const togglePopup = React.useCallback(
    key => {
      if (key === visibleMarkerPopup) {
        setVisibleMarkerPopup();
      } else {
        setVisibleMarkerPopup(key);
      }
    },
    [visibleMarkerPopup],
  );

  const closePopup = React.useCallback(() => {
    setVisibleMarkerPopup();
  }, []);

  const markers = [];
  const popups = [];
  visibleRoutes.forEach(route => {
    route.points.forEach(point => {
      const props = {
        coordinates: [point.position.lng, point.position.lat],
        key: point.key,
      };
      switch (point.type) {
        case 'start':
          markers.push(
            <Marker
              {...props}
              onClick={() => {
                togglePopup(route.key + 'start');
              }}
            >
              {greenMarker}
            </Marker>,
          );
          popups.push(
            <Popup
              {...props}
              offset={[0, -35]}
              className="route-popup"
              style={{
                display: visibleMarkerPopup === route.key + point.key ? 'flex' : 'none',
              }}
            >
              <StartPopupContent t={t} point={point} />
            </Popup>,
          );
          break;
        case 'end':
          markers.push(
            <Marker
              {...props}
              onClick={() => {
                togglePopup(route.key + 'end');
              }}
            >
              {redMarker}
            </Marker>,
          );
          popups.push(
            <Popup
              {...props}
              offset={[0, -35]}
              className="route-popup"
              style={{
                display: visibleMarkerPopup === route.key + point.key ? 'flex' : 'none',
              }}
            >
              <FinishPopupContent t={t} point={point} />
            </Popup>,
          );
          break;
        default:
          break;
      }
    });
  });

  useEffect(() => {
    const bounds = [];

    routes.forEach(route => {
      if (route.polyline && route.polyline.length > 0) {
        route.polyline.forEach(point => {
          bounds.push({ lat: point.lat, lng: point.lng });
        });
      }
    });

    setDebouncedMapBounds(getMapBoundaries(bounds));
  }, [routes, setDebouncedMapBounds]);

  return (
    <Map
      center={config.center}
      fitBounds={debouncedMapBounds.length > 1 ? debouncedMapBounds : undefined}
      fitBoundsOptions={{ padding: 20 }}
      // eslint-disable-next-line
      style="mapbox://styles/mapbox/streets-v12"
      containerStyle={{ height: '100%', width: '100%' }}
      onClick={closePopup}
      onRender={setMapInstance}
      onStyleLoad={setIsMapLoaded}
    >
      {isMapLoaded && polylines}

      {markers}
      {popups}
      <ZoomControl position="top-left" />
    </Map>
  );
};

MapboxMap.defaultProps = {};
